import { useState } from "react";
import { MesesList } from "./sections/Mock";
import { Filtros } from "./sections/Filtros";
import { useQuery } from "@tanstack/react-query";
import UsersService from "@/services/users";
import ImageGeneric from "@/assets/images/lg.png";
import moment from "moment";
import * as ExcelJS from "exceljs";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Props {
  idMenu: any;
}

export function Aniversariantes({ idMenu }: Props) {
  const { t } = useTranslation();
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [type_user, setType_user] = useState<any>("");
  const [agencySelect, setAgencySelect] = useState<any>("");
  const [representanteSelect, setRepresentanteSelect] = useState<any>("");
  const [representadoSelect, setRepresentadoSelect] = useState<any>("");
  const [regiaoSelect, setRegiaoSelect] = useState<any>("");
  const [areaSelecionada, setAreaSelecionada] = useState<any>("");
  const [cargoSelecionado, setCargoSelecionado] = useState<any>("");
  const [cargos, setCargos] = useState<Array<any>>([]);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const mesAtual: any = new Date().getMonth();

  const [mes, setMes] = useState({
    value: MesesList[mesAtual].value,
    label: MesesList[mesAtual].label,
  });

  const { isPending, data } = useQuery({
    queryKey: [
      "aniversariantes",
      mes?.value,
      currentPage,
      type_user,
      agencySelect,
      cargoSelecionado,
      areaSelecionada,
      regiaoSelect,
      representanteSelect,
      representadoSelect,
    ],
    queryFn: async () =>
      await UsersService.aniversariantes(
        mes?.value,
        currentPage,
        5,
        type_user?.value,
        agencySelect?.value,
        cargoSelecionado?.value,
        areaSelecionada?.value,
        regiaoSelect?.value,
        representanteSelect?.value,
        representadoSelect?.value
      ),
    enabled: idMenu === "aniversariantes",
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  function getUserTypeName(typeUserId: number) {
    switch (typeUserId) {
      case 1:
        return t("common:user_types.go_on");
      case 2:
        return t("common:user_types.agency");
      case 3:
        return t("common:user_types.representative");
      case 4:
        return t("common:user_types.represented");
      case 5:
        return t("common:user_types.seller");
      case 6:
        return t("common:user_types.client");
      default:
        return "-";
    }
  }

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Buscar todos os dados com página grande
      const response = await UsersService.aniversariantes(
        mes?.value,
        1,
        999,
        type_user?.value,
        agencySelect?.value,
        cargoSelecionado?.value,
        areaSelecionada?.value,
        regiaoSelect?.value,
        representanteSelect?.value,
        representadoSelect?.value
      );

      if (response?.data?.data) {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Aniversariantes");

        // Definir cabeçalhos
        worksheet.columns = [
          { header: t("birthdays.excel.name"), key: "name", width: 25 },
          { header: t("birthdays.excel.last_name"), key: "last_name", width: 25 },
          { header: t("birthdays.excel.email"), key: "email", width: 30 },
          { header: t("birthdays.excel.birth_date"), key: "birth_date", width: 20 },
          { header: t("birthdays.excel.user_type"), key: "type_user", width: 20 },
        ];

        // Adicionar dados
        response.data.data.forEach((item: any) => {
          worksheet.addRow({
            name: item.name,
            last_name: item.last_name,
            email: item.email,
            birth_date: moment(item.birth_date).format("DD/MM"),
            type_user: getUserTypeName(item.type_user_id),
          });
        });

        // Estilizar cabeçalho
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0E0" },
        };

        // Gerar arquivo e download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `aniversariantes_${
          mes.label
        }_${new Date().getFullYear()}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Erro ao exportar:", error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col justify-between gap-1">
        <h2 className="text-xl font-bold">{t("birthdays.title")}</h2>
        <button
          onClick={exportToExcel}
          disabled={exportLoading || isPending}
          className="hidden w-fit items-center gap-2 px-4 py-2 bg-verde-300 text-white rounded-lg hover:bg-green-700 duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
        >
          <Download size={16} />
          {exportLoading ? t("birthdays.exporting") : t("birthdays.download_excel")}
        </button>
      </div>
      <div>
        <Filtros
          type_user={type_user}
          setType_user={setType_user}
          agencySelect={agencySelect}
          setAgencySelect={setAgencySelect}
          representanteSelect={representanteSelect}
          setRepresentanteSelect={setRepresentanteSelect}
          mes={mes}
          setMes={setMes}
          representadoSelect={representadoSelect}
          setRepresentadoSelect={setRepresentadoSelect}
          regiaoSelect={regiaoSelect}
          setRegiaoSelect={setRegiaoSelect}
          areaSelecionada={areaSelecionada}
          setAreaSelecionada={setAreaSelecionada}
          cargoSelecionado={cargoSelecionado}
          setCargoSelecionado={setCargoSelecionado}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          mesAtual={mesAtual}
          idMenu={idMenu}
          cargos={cargos}
          setCargos={setCargos}
        />
      </div>
      <div className="mt-6">
        <ul className="space-y-4">
          {!!isPending && (
            <>
              {Array.from({ length: 5 }).map((_, index) => (
                <li
                  key={index}
                  className="p-2 bg-gray-100 rounded-lg shadow-md flex justify-between items-center animate-pulse"
                >
                  <div className="w-full flex items-center gap-x-4">
                    <div className="flex gap-2 items-center w-2/12">
                      <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                    </div>
                    <div className="w-10/12 space-y-2">
                      <div className="h-4 bg-gray-300 rounded w-full"></div>
                      <div className="h-4 bg-gray-300 rounded w-full"></div>
                      <div className="h-4 bg-gray-300 rounded w-full"></div>
                    </div>
                  </div>
                </li>
              ))}
            </>
          )}
          {!isPending &&
            data?.data?.data?.map((item: any) => (
              <li
                key={item?.id}
                className="p-4 bg-gray-100 rounded-lg shadow-md flex justify-between items-center relative"
              >
                <div className="w-full flex justify-between items-start ">
                  <div className="flex gap-2 items-center">
                    <img
                      src={item?.filePath ?? ImageGeneric}
                      alt={item?.name}
                      className="w-10 h-10 object-cover rounded-full"
                    />
                    <div>
                      <p className="font-semibold">
                        {item?.name} {item?.last_name} |{" "}
                        {moment(item?.birth_date).format("DD/MM")}
                      </p>
                      <p className="text-xs text-gray-500">{item?.email}</p>
                      <p className="text-xs text-white bg-azul-400 px-2 py-px rounded-lg inline-flex absolute -top-2 right-0">
                        {getUserTypeName(item?.type_user_id)}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            ))}
        </ul>

        {data?.data?.qtdRegistros == 0 && (
          <p className="text-gray-500 text-center">
            {t("birthdays.no_birthdays_found")}
          </p>
        )}
        {!isPending && data?.data?.qtdRegistros >= 5 && (
          <div className="flex items-center justify-center mt-6 space-x-4 text-sm">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-azul-400 text-white rounded-lg disabled:bg-gray-400"
            >
              {t("common:pagination.previous")}
            </button>
            <span className="text-sm">
              {t("common:pagination.page")} {currentPage} {t("common:pagination.of")} {Math.ceil(data?.data?.qtdRegistros / 5)}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === Math.ceil(data?.data?.qtdRegistros / 5)}
              className="px-3 py-2 bg-azul-400 text-white rounded-lg disabled:bg-gray-400"
            >
              {t("common:pagination.next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
